import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import { Column } from '../../../components/DataTable/DataTable';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { deleteSparePurchaseOrder } from '../../../services/mutations';
import { fetcher } from '../../../services/fetcher';
import type { SparePurchaseOrder } from './useData';
import { computePurchaseOrderTotals } from './purchaseOrderTotals';
import { supplyListMoneySpanClass, SupplyListLineItemCountCell } from '../spareListMoneyDisplay';
import { supplyBlockedActionMessage } from '../blockingMessages';

export const moduleConfig = {
  module: {
    title: 'Purchase orders',
    permissionModuleKey: 'spare_purchase_orders',
    route: {
      list: '/spares/spare-purchase-orders',
      new: '/spares/spare-purchase-orders/new',
      detail: (id: number) => `/spares/spare-purchase-orders/${id}`,
      edit: (id: number) => `/spares/spare-purchase-orders/${id}/edit`},
    api: {
      list: '/spare-purchase-orders',
      detail: (id: number) => `/spare-purchase-orders/${id}`}},
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions},
  search: { placeholder: 'Search by reference, notes, case, quotation or supplier…' },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '',
      fields: ['vessel', 'ref', 'orderDate', 'caseRef', 'quotationRef', 'supplier']},
  ],
  filters: {
    fields: [
      {
        key: 'vessel',
        label: 'Vessel',
        type: 'select' as const,
        placeholder: 'All vessels',
        getValue: (item: SparePurchaseOrder) => item.spareCase?.vessel?.vesselName?.trim() || '',
        options: []},
      {
        key: 'ref',
        label: 'Reference',
        placeholder: 'All references',
        getValue: (item: SparePurchaseOrder) => item.ref || '',
        options: []},
      {
        key: 'orderDate',
        label: 'Order date',
        type: 'date' as const,
        placeholder: 'All dates',
        getValue: (item: SparePurchaseOrder) => item.orderDate || '',
        options: []},
      {
        key: 'caseRef',
        label: 'Case',
        type: 'select' as const,
        placeholder: 'All cases',
        getValue: (item: SparePurchaseOrder) => item.spareCase?.ref || '',
        options: []},
      {
        key: 'quotationRef',
        label: 'Quotation',
        type: 'select' as const,
        placeholder: 'All quotations',
        getValue: (item: SparePurchaseOrder) => item.quotation?.ref || '',
        options: []},
      {
        key: 'supplier',
        label: 'Supplier',
        type: 'select' as const,
        placeholder: 'All suppliers',
        getValue: (item: SparePurchaseOrder) => item.supplier?.name || '',
        options: []},
    ],
    applyFilter: (
      items: SparePurchaseOrder[],
      filters: Record<string, string[] | string>,
      dateRanges?: Record<string, { start: Date | null; end: Date | null }>,
    ) => {
      const asArray = (v: string[] | string | undefined): string[] =>
        Array.isArray(v) ? v : typeof v === 'string' && v !== '' ? [v] : [];
      const toDateOnly = (s?: string | null): string => (s ? String(s).slice(0, 10) : '');
      return items.filter((item) => {
        const vesselFilters = asArray(filters.vessel);
        if (vesselFilters.length) {
          const vesselName = (item.spareCase?.vessel?.vesselName || '').toLowerCase();
          if (!vesselFilters.some((f) => vesselName.includes(String(f).toLowerCase()))) return false;
        }

        const refFilters = asArray(filters.ref);
        if (refFilters.length) {
          const v = item.ref || '';
          if (!refFilters.some((f) => v.toLowerCase().includes(String(f).toLowerCase()))) return false;
        }

        const orderDateFilter = dateRanges?.orderDate;
        if (orderDateFilter?.start || orderDateFilter?.end) {
          const itemDateRaw = item.orderDate ? new Date(item.orderDate) : null;
          if (!itemDateRaw || Number.isNaN(itemDateRaw.getTime())) return false;
          if (orderDateFilter.start && itemDateRaw < orderDateFilter.start) return false;
          if (orderDateFilter.end && itemDateRaw > orderDateFilter.end) return false;
        } else {
          const orderDateValues = asArray(filters.orderDate);
          if (orderDateValues.length) {
            const itemDate = toDateOnly(item.orderDate);
            if (!orderDateValues.some((f) => itemDate.includes(String(f).slice(0, 10)))) return false;
          }
        }

        const caseFilters = asArray(filters.caseRef);
        if (caseFilters.length) {
          const c = (item.spareCase?.ref || '').toLowerCase();
          if (!caseFilters.some((f) => c.includes(String(f).toLowerCase()))) return false;
        }

        const quotationFilters = asArray(filters.quotationRef);
        if (quotationFilters.length) {
          const q = (item.quotation?.ref || '').toLowerCase();
          if (!quotationFilters.some((f) => q.includes(String(f).toLowerCase()))) return false;
        }

        const supplierFilters = asArray(filters.supplier);
        if (supplierFilters.length) {
          const s = (item.supplier?.name || '').toLowerCase();
          if (!supplierFilters.some((f) => s.includes(String(f).toLowerCase()))) return false;
        }
        return true;
      });
    },
    applySearch: (items: SparePurchaseOrder[], searchTerm: string) => {
      const q = searchTerm.toLowerCase();
      return items.filter((item) => {
        const parts = [
          item.ref,
          item.notes,
          item.paymentTerms,
          item.spareCase?.ref,
          item.quotation?.ref,
          String(item.id),
          String(item.supplierId),
          String(item.quotationId),
          item.supplier?.name,
          item.supplier?.code,
          item.currency?.code,
          item.currency?.name,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return parts.includes(q);
      });
    }},
  toolbar: {
    actions: []},
  table: {
    actionsPosition: 'left' as const,
    keyExtractor: (item: SparePurchaseOrder) => item.id,
  },
  emptyStates: {
    noData: 'Create a purchase order to get started.',
    noSearchResults: 'No purchase orders match your search.',
    noFilterResults: 'No purchase orders match the filters.',
  },
  delete: {
    title: 'Delete purchase order',
    message: 'Delete this purchase order and all its lines?',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Could not delete purchase order.',
    handler: async (id: number) => {
      await deleteSparePurchaseOrder(id);
    }},
  utils: {
    formatDate: (dateString?: string) => (dateString ? formatDateGlobal(dateString) : '')},
  form: {
    new: { title: 'New purchase order', subtitle: 'Custom form', submitText: 'Create' },
    edit: { title: 'Edit purchase order', submitText: 'Save' },
    cancelText: 'Cancel',
    loadingText: 'Loading...',
    errorMessage: 'Save failed.'},
  fields: [] as FieldConfig[]};

const TextCell: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  const { colors } = useTheme();
  return <span className={cn('text-sm', colors.text)}>{value || ''}</span>;
};

/** Path must stay in sync with `moduleConfig.route.caseView` in `spare-cases/config.tsx`. */
function supplyPurchaseOrderCaseIdForView(row: SparePurchaseOrder | undefined): number | null {
  const id = row?.spareCase?.id ?? row?.caseId ?? 0;
  return id > 0 ? id : null;
}

const PurchaseOrderCaseColumnCell: React.FC<{ row: SparePurchaseOrder }> = ({ row }) => {
  const { colors } = useTheme();
  const caseId = supplyPurchaseOrderCaseIdForView(row);
  if (caseId == null) {
    return (
      <div className="max-w-[16rem] py-0.5">
        <span className={cn('text-sm', colors.textSecondary)}>—</span>
      </div>
    );
  }
  const displayRef = row.spareCase?.ref?.trim() || `Case #${caseId}`;
  const href = `/spares/spare-cases/${caseId}/case-view`;
  const iconBtnClass =
    'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-indigo-600 transition-colors hover:bg-indigo-50 hover:text-indigo-900 dark:text-indigo-400 dark:hover:bg-indigo-950/50 dark:hover:text-indigo-300';

  return (
    <div className="flex max-w-[16rem] items-center gap-0.5 py-0.5">
      <Link
        to={href}
        className={iconBtnClass}
        title="Open case view"
        aria-label="Open case view"
        onClick={(e) => e.stopPropagation()}
      >
        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
          />
        </svg>
      </Link>
      <span className={cn('min-w-0 flex-1 truncate text-sm', colors.text)} title={displayRef}>
        {displayRef}
      </span>
    </div>
  );
};

const fmtMoney = (n: number, suffix?: string) =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n)}${suffix ? ` ${suffix}` : ''}`;

const POMoneyCell: React.FC<{
  order: SparePurchaseOrder | undefined;
  variant: 'expenses' | 'items' | 'grand' | 'usd';
}> = ({ order, variant }) => {
  const { colors } = useTheme();
  if (!order) {
    return (
      <span className={cn('block w-full min-w-[6.75rem] text-right text-sm', colors.textSecondary)}>—</span>
    );
  }
  const t = computePurchaseOrderTotals(order);
  const ccy = order.currency?.code ?? '';
  if (variant === 'expenses') {
    return (
      <span
        className={supplyListMoneySpanClass('expenses', colors)}
        title={
          ccy
            ? `Sum of expense value + VAT in order currency (${ccy})`
            : 'Sum of expense value + VAT in order currency'
        }
      >
        {fmtMoney(t.expensesSum)}
      </span>
    );
  }
  if (variant === 'items') {
    return (
      <span
        className={supplyListMoneySpanClass('items', colors)}
        title={
          ccy ? `Sum of line totals in order currency (${ccy})` : 'Sum of line totals in order currency'
        }
      >
        {fmtMoney(t.itemsSum)}
      </span>
    );
  }
  if (variant === 'grand') {
    return (
      <span
        className={supplyListMoneySpanClass('grand', colors)}
        title={
          ccy
            ? `Items sum + expenses sum in order currency (${ccy})`
            : 'Items sum + expenses sum in order currency'
        }
      >
        {fmtMoney(t.grandTotal)}
      </span>
    );
  }
  return (
    <span
      className={supplyListMoneySpanClass('usd', colors, t.grandTotalUsd != null)}
      title="(Line totals + expenses) × exchange rate, in USD"
    >
      {t.grandTotalUsd != null ? fmtMoney(t.grandTotalUsd) : '—'}
    </span>
  );
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<SparePurchaseOrder>[] => [
  {
    key: 'vessel',
    header: 'Vessel',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.spareCase?.vessel?.vesselName} />},
  { key: 'ref', header: 'Reference', sortable: true, render: (v, row) => <TextCell value={v ?? row?.ref} /> },
  { key: 'orderDate', header: 'Order date', sortable: true, render: (v) => formatDate(v) },
  {
    key: 'spareCase.ref',
    header: 'Case',
    sortable: true,
    maxWidth: '16rem',
    render: (_v, row) => (row ? <PurchaseOrderCaseColumnCell row={row} /> : <TextCell value="" />)},
  {
    key: 'quotation.ref',
    header: 'Quotation',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.quotation?.ref ?? (row?.quotationId != null ? `#${row.quotationId}` : '')} />},
  {
    key: 'supplier.name',
    header: 'Supplier',
    sortable: true,
    render: (_v, row) => (
      <TextCell
        value={
          row?.supplier?.name
            ? row.supplier.name
            : row?.supplierId != null
              ? String(row.supplierId)
              : ''
        }
      />
    )},
  {
    key: 'lineItems',
    header: 'Items',
    sortable: true,
    align: 'right',
    render: (_v, row) => (
      <SupplyListLineItemCountCell count={row?._count?.lineItems ?? row?.lineItems?.length ?? 0} />
    )},
  {
    key: 'currency.code',
    header: 'CCY',
    sortable: true,
    render: (_v, row) => <TextCell value={row?.currency?.code} />},
  {
    key: 'expensesSum',
    header: 'Expenses',
    sortable: false,
    align: 'right',
    render: (_v, row) => <POMoneyCell order={row} variant="expenses" />},
  {
    key: 'itemsSum',
    header: 'Items Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <POMoneyCell order={row} variant="items" />},
  {
    key: 'grandTotal',
    header: 'Grand Total',
    sortable: false,
    align: 'right',
    render: (_v, row) => <POMoneyCell order={row} variant="grand" />},
  {
    key: 'grandTotalUsd',
    header: 'Grand Total ($)',
    sortable: false,
    align: 'right',
    render: (_v, row) => <POMoneyCell order={row} variant="usd" />},
  { key: 'createdAt', header: 'Created', sortable: true, render: (v) => formatDate(v) },
];

const TableActionsCell: React.FC<{
  item: SparePurchaseOrder;
  onDelete: (id: number) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}> = ({ item, onDelete, canDelete = true, canEdit = true }) => {
  const location = useLocation();
  const editReturnState = { from: `${location.pathname}${location.search}` };
  const { showError } = useNotification();
  const { data: forwardingsRes } = useSWR<any>('/spare-forwardings?page=1&limit=1000', fetcher);
  const { data: deliveriesRes } = useSWR<any>('/spare-deliveries?page=1&limit=1000', fetcher);
  const { data: invoicesRes } = useSWR<any>('/spare-invoices?page=1&limit=1000', fetcher);

  const hasCaseAssigned = (item.caseId ?? 0) > 0;
  const hasForwardingLinked = React.useMemo(() => {
    const rows = Array.isArray(forwardingsRes) ? forwardingsRes : forwardingsRes?.data ?? [];
    return rows.some((f: any) => Number(f?.purchaseOrderId) === item.id);
  }, [forwardingsRes, item.id]);
  const hasDeliveryLinked = React.useMemo(() => {
    const rows = Array.isArray(deliveriesRes) ? deliveriesRes : deliveriesRes?.data ?? [];
    return rows.some((d: any) => Number(d?.purchaseOrderId) === item.id);
  }, [deliveriesRes, item.id]);
  const hasInvoiceLinked = React.useMemo(() => {
    const rows = Array.isArray(invoicesRes) ? invoicesRes : invoicesRes?.data ?? [];
    return rows.some((i: any) => Number(i?.purchaseOrderId) === item.id);
  }, [invoicesRes, item.id]);

  const deleteEnabled =
    canDelete && !hasCaseAssigned && !hasForwardingLinked && !hasDeliveryLinked && !hasInvoiceLinked;
  const editEnabled = canEdit && !hasForwardingLinked && !hasDeliveryLinked && !hasInvoiceLinked;

  const onDeleteBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasForwardingLinked) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('PO_DELETE_FORWARDING')}`);
      return;
    }
    if (hasDeliveryLinked) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('PO_DELETE_DELIVERY')}`);
      return;
    }
    if (hasInvoiceLinked) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('PO_DELETE_INVOICE')}`);
      return;
    }
    if (hasCaseAssigned) {
      showError(`Cannot delete: ${supplyBlockedActionMessage('PO_DELETE_CASE_ASSIGNED')}`);
    }
  };

  const onEditBlocked = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (hasForwardingLinked) {
      showError(`Cannot edit: ${supplyBlockedActionMessage('PO_EDIT_FORWARDING')}`);
      return;
    }
    if (hasDeliveryLinked) {
      showError(`Cannot edit: ${supplyBlockedActionMessage('PO_EDIT_DELIVERY')}`);
      return;
    }
    if (hasInvoiceLinked) {
      showError(`Cannot edit: ${supplyBlockedActionMessage('PO_EDIT_INVOICE')}`);
    }
  };
  return (
  <>
  <div className="flex items-center flex-nowrap">
    <Link
      to={moduleConfig.module.route.detail(item.id)}
      className="p-1 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
      onClick={(e) => e.stopPropagation()}
      title="View"
    >
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    </Link>
    {canEdit && editEnabled ? (
      <Link
        to={moduleConfig.module.route.edit(item.id)}
        state={editReturnState}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </Link>
    ) : canEdit ? (
      <button
        type="button"
        onClick={onEditBlocked}
        className="p-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
        title="Edit"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>
    ) : null}
    {canDelete && deleteEnabled ? (
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete(item.id);
        }}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    ) : canDelete ? (
      <button
        type="button"
        onClick={onDeleteBlocked}
        className="p-1 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
        title="Delete"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    ) : null}
  </div>
  </>
  );
};

export const getTableActions = (
  item: SparePurchaseOrder,
  onDelete: (id: number) => void,
  _c?: unknown,
  _ctx?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean },
): React.ReactNode => (
  <TableActionsCell item={item} onDelete={onDelete} canDelete={options?.canDelete !== false} canEdit={options?.canEdit !== false} />
);

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  if (iconName === 'add') {
    return (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    );
  }
  return null;
};
